import { FormInstance } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectBaseline } from 'api/project';
import { Form } from 'components/atoms/form';
import { Input, InputType, TextArea } from 'components/atoms/input';
import { useTranslate } from 'hooks/use-translation';

interface Props {
  form: FormInstance;
  baselines: ProjectBaseline[];
  currentBaseline?: ProjectBaseline;
}

export interface BaselineFormProps {
  name: string;
  notes?: string;
}

const StyledForm = styled(Form)`
  margin-top: var(--spacing-05);
`;

export const BaselineForm = ({ form, baselines, currentBaseline }: Props) => {
  const translate = useTranslate();
  const inputRef = useRef<InputType>(null);
  const requiredErrorMessage = translate('input.required');
  const namePlaceholder = translate('baseline.name.placeholder');
  const notesPlaceholder = translate('baseline.notes.placeholder');

  const currentBaselineName = (
    currentBaseline?.baselineName || ''
  ).toLowerCase();

  const getBaselineNames = useCallback(() => {
    return Object.values(baselines).reduce<string[]>(
      (prev, current) =>
        currentBaselineName === current.baselineName?.toLowerCase()
          ? prev
          : [...prev, current.baselineName?.toLowerCase()],
      [],
    );
  }, [baselines, currentBaselineName]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <StyledForm form={form} layout="vertical" autoComplete="off">
      <Form.Item
        required
        name="name"
        label={<FormattedMessage id="name" />}
        rules={[
          { required: true, message: requiredErrorMessage },
          {
            validator: (_, value) => {
              const existing = getBaselineNames();
              const formattedValue = (value || '').toLowerCase().trim();

              if (
                existing?.find(
                  (_name) => _name.toLowerCase().trim() === formattedValue,
                )
              ) {
                return Promise.reject(new Error('Name already exists'));
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input ref={inputRef} placeholder={namePlaceholder} />
      </Form.Item>
      <Form.Item
        name="notes"
        label={<FormattedMessage id="baseline.notes.label" />}
      >
        <TextArea placeholder={notesPlaceholder} />
      </Form.Item>
    </StyledForm>
  );
};
